import { createContext, useContext } from "react";

import {
  type PopoverContentProps,
  createPopoverHandle,
} from "@frontend/components/atoms/Popover";

import {
  type ContactPreviewResult,
  loadContact,
  peekContact,
} from "./contactPreviewCache";

export const contactPreviewHandle = createPopoverHandle<number>();

export type ContactPreviewTarget = {
  triggerId: string;
  contactId: number;
  side: PopoverContentProps["side"];
};

export type ContactPreviewGroupState = {
  pending: ContactPreviewTarget | null;
  target: ContactPreviewTarget | null;
  shown: ContactPreviewResult | null;
  open: boolean;
  hostId: string | null;
};

let group: ContactPreviewGroupState = {
  pending: null,
  target: null,
  shown: null,
  open: false,
  hostId: null,
};

const listeners = new Set<() => void>();

function commit(next: ContactPreviewGroupState): void {
  group = next;

  for (const listener of [...listeners]) {
    listener();
  }
}

export function subscribeGroup(listener: () => void): () => void {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

export function getGroup(): ContactPreviewGroupState {
  return group;
}

// Asks for a contact, and moves the card onto it once it is in hand.
export function requestContact(target: ContactPreviewTarget): void {
  const known = peekContact(target.contactId);

  if (known !== undefined) {
    commit({ ...group, pending: null, target, shown: known, open: true });
  } else {
    commit({ ...group, pending: target });

    void loadContact(target.contactId).then((shown) => {
      if (group.pending?.triggerId !== target.triggerId) {
        return;
      }

      commit({ ...group, pending: null, target, shown, open: true });
    });
  }

  prefetchNeighbours(target.triggerId);
}

export function closeContact(): void {
  commit({ ...group, pending: null, open: false });
}

type MountedTrigger = {
  triggerId: string;
  contactId: number;
};

const mounted: MountedTrigger[] = [];

export function registerTrigger(trigger: MountedTrigger): () => void {
  mounted.push(trigger);
  commit({ ...group, hostId: mounted[0].triggerId });

  return () => {
    const at = mounted.findIndex(
      (candidate) => candidate.triggerId === trigger.triggerId,
    );

    if (at !== -1) {
      mounted.splice(at, 1);
    }

    commit({ ...group, hostId: mounted[0]?.triggerId ?? null });
  };
}

const PREFETCH_RADIUS = 4;

function prefetchNeighbours(triggerId: string): void {
  const at = mounted.findIndex(
    (candidate) => candidate.triggerId === triggerId,
  );

  if (at === -1) {
    return;
  }

  const from = Math.max(0, at - PREFETCH_RADIUS);
  const to = Math.min(mounted.length, at + PREFETCH_RADIUS + 1);

  for (let index = from; index < to; index += 1) {
    if (index !== at) {
      void loadContact(mounted[index].contactId);
    }
  }
}

export type ContactPreviewTriggerContext = {
  triggerId: string;
  contactId: number;
  loading: boolean;
  cardOpen: boolean;
  activate: () => void;
  onPointerEnter: (pointerType: string) => void;
  onPointerLeave: () => void;
};

const ignore = () => {};

export const ContactPreviewTriggerContext =
  createContext<ContactPreviewTriggerContext>({
    triggerId: "",
    contactId: 0,
    loading: false,
    cardOpen: false,
    activate: ignore,
    onPointerEnter: ignore,
    onPointerLeave: ignore,
  });

export function useContactPreviewTrigger(): ContactPreviewTriggerContext {
  return useContext(ContactPreviewTriggerContext);
}
